import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { fmtDateTime, tl, stat } from "@/lib/i18nFormat";
import { PageHeader, LoadingBlock, EmptyState, StatusPill } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ListChecks, ShieldCheck, ShieldAlert, Clock, Paperclip, AlertTriangle, Send, LinkIcon } from "lucide-react";

import { useI18n } from "@/context/I18nContext";

const STATUS_MAP: Record<string, string> = {
  upcoming: "info", available: "info", in_progress: "warn", blocked: "critical",
  awaiting_verification: "warn", completed: "good", correction_required: "critical",
  overdue: "critical", cancelled: "inactive",
};
const STATUS_LABEL: Record<string, string> = {
  upcoming: "Upcoming", available: "Available", in_progress: "In progress", blocked: "Blocked",
  awaiting_verification: "Awaiting verification", completed: "Completed",
  correction_required: "Correction required", overdue: "Overdue", cancelled: "Cancelled",
};
const PRIORITY_MAP: Record<string, string> = { normal: "info", important: "warn", urgent: "critical" };
const EDITABLE = ["available", "in_progress", "blocked", "correction_required", "overdue"];

export const TaskExecution: React.FC<{ todayOnly?: boolean; emptyHint?: string }> = ({ todayOnly, emptyHint }) => {
  const {
    t: tI18n
  } = useI18n();

  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [excFor, setExcFor] = useState<string | null>(null);
  const [excReason, setExcReason] = useState("");
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/tasks/my-tasks${todayOnly ? "?today=true" : ""}`)
      .then((r) => setTasks(r.data.tasks))
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  }, [todayOnly]);
  useEffect(() => { load(); }, [load]);

  const patch = (id: string, changes: any) =>
    setTasks((prev) => prev.map((x) => (x.instance_id === id ? { ...x, ...changes } : x)));

  const toggle = async (t: any, itemId: string, done: boolean) => {
    const key = `${t.instance_id}:${itemId}`;
    setBusy(key);
    try {
      const verb = done ? "undo" : "complete";
      const r = await api.post(`/tasks/instances/${t.instance_id}/items/${itemId}/${verb}`, {});
      patch(t.instance_id, { status: r.data.status, completed_item_ids: r.data.completed_item_ids });
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(null); }
  };

  const submit = async (t: any) => {
    setBusy(`submit:${t.instance_id}`);
    try {
      const r = await api.post(`/tasks/instances/${t.instance_id}/submit`, { client_id: `${t.instance_id}-submit` });
      patch(t.instance_id, { status: r.data.status });
      toast.success(r.data.status === "completed" ? tl("Task complete") : tl("Submitted for verification"));
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Submit failed"); }
    finally { setBusy(null); }
  };

  const reportException = async (t: any) => {
    if (!excReason.trim()) { toast.error("Describe the blocker"); return; }
    try {
      await api.post(`/tasks/instances/${t.instance_id}/exception`, { reason: excReason.trim() });
      toast.success("Blocker reported");
      setExcFor(null); setExcReason(""); load();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const createAction = async (t: any) => {
    try {
      const r = await api.post(`/tasks/instances/${t.instance_id}/exception/action`, {});
      patch(t.instance_id, { linked_action_id: r.data.action_id });
      toast.success(r.data.created ? tl("Persistent Action created") : tl("Action already linked"));
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const uploadEvidence = async (t: any, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/tasks/instances/${t.instance_id}/evidence`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      patch(t.instance_id, { evidence: [...(t.evidence || []), r.data] });
      toast.success("Evidence attached");
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  const download = async (attId: string, filename: string) => {
    try {
      const r = await api.get(`/tasks/evidence/${attId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a"); link.href = url; link.download = filename; link.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Not authorized"); }
  };

  if (loading) return <LoadingBlock />;
  if (tasks.length === 0) {
    return <EmptyState icon={<ListChecks size={28} />} title="No tasks"
      description={emptyHint || "Tasks assigned to your store will appear here."} />;
  }

  return (
    (<div className="space-y-4" data-testid="mywork-list">
      {tasks.map((t) => {
        const done = new Set<string>(t.completed_item_ids || []);
        const total = t.checklist.length;
        const doneCount = t.checklist.filter((i: any) => done.has(i.item_id)).length;
        const requiredDone = t.checklist.filter((i: any) => i.required).every((i: any) => done.has(i.item_id));
        const editable = EDITABLE.includes(t.status);
        const evidenceOk = !t.evidence_required || (t.evidence || []).length > 0;
        const canSubmit = editable && requiredDone && evidenceOk;
        return (
          (<Card key={t.instance_id} className="p-4" data-testid={`task-card-${t.instance_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-display text-base font-semibold">{t.title}</span>
                  <StatusPill status={PRIORITY_MAP[t.priority] || "info"}>{stat(t.priority)}</StatusPill>
                </div>
                {t.instructions && <p className="mt-0.5 text-sm text-muted-foreground">{t.instructions}</p>}
                <div className="mt-1 text-xs text-muted-foreground">{tI18n("Store")}{" "}{t.store_number}· {t.store_name}
                  {t.due_at ? ` · ${tl("due")} ${fmtDateTime(t.due_at)}` : ""}
                  {t.occurrence_date ? ` · ${t.occurrence_date}` : ""}
                </div>
              </div>
              <StatusPill status={STATUS_MAP[t.status] || "info"} testId={`task-status-${t.instance_id}`}>
                {STATUS_LABEL[t.status] || t.status}
              </StatusPill>
            </div>
            <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
              <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${total ? (100 * doneCount) / total : 0}%` }} />
            </div>
            {t.status !== "upcoming" && (
              <div className="mt-3 space-y-2">
                {t.checklist.map((item: any) => {
                  const isDone = done.has(item.item_id);
                  const key = `${t.instance_id}:${item.item_id}`;
                  return (
                    (<label key={item.item_id}
                        className={`flex items-start gap-3 rounded-md border border-border p-2.5 ${editable ? "cursor-pointer hover:border-brand/40" : "opacity-70"}`}
                        data-testid={`task-item-${item.item_id}`}>
                      <Checkbox checked={isDone} disabled={busy === key || !editable}
                        onCheckedChange={() => toggle(t, item.item_id, isDone)}
                        data-testid={`task-check-${item.item_id}`} />
                      <div className="text-sm">
                        <span className={isDone ? tI18n("text-muted-foreground line-through") : ""}>{item.text}</span>
                        {!item.required && <span className="ml-2 text-[11px] text-muted-foreground">{tI18n("(optional)")}</span>}
                      </div>
                    </label>)
                  );
                })}
              </div>
            )}
            {/* Evidence */}
            {(t.evidence_required || (t.evidence || []).length > 0) && t.status !== "upcoming" && (
              <div className="mt-3">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase text-muted-foreground">
                  <Paperclip size={12} />{tI18n("Evidence")}{t.evidence_required ? tI18n("(required)") : ""}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  {(t.evidence || []).map((e: any) => (
                    <button key={e.id} onClick={() => download(e.id, e.filename)} className="text-xs text-brand hover:underline" data-testid={`task-evidence-${e.id}`}>{e.filename}</button>
                  ))}
                  {editable && (
                    <>
                      <input type="file" hidden ref={(el) => { fileRefs.current[t.instance_id] = el; }}
                        onChange={(ev) => { const f = ev.target.files?.[0]; if (f) uploadEvidence(t, f); ev.target.value = ""; }}
                        data-testid={`task-evidence-input-${t.instance_id}`} />
                      <Button variant="outline" size="sm" onClick={() => fileRefs.current[t.instance_id]?.click()} data-testid={`task-evidence-upload-${t.instance_id}`}>
                        <Paperclip size={13} className="mr-1" />{tI18n("Attach")}</Button>
                    </>
                  )}
                </div>
              </div>
            )}
            {/* Status banners */}
            {t.exception && !["completed", "awaiting_verification"].includes(t.status) && (
              <div className="mt-3 flex items-center gap-1.5 text-sm text-status-critical" data-testid={`task-blocked-${t.instance_id}`}>
                <AlertTriangle size={15} />{tI18n("Blocked:")}{" "}{t.exception.reason}
              </div>
            )}
            {t.status === "upcoming" && <div className="mt-3 flex items-center gap-1.5 text-sm text-status-info"><Clock size={15} />{tI18n("Opens")}{" "}{t.occurrence_date}</div>}
            {t.status === "awaiting_verification" && <div className="mt-3 flex items-center gap-1.5 text-sm text-status-warn"><Clock size={15} />{tI18n("Submitted — awaiting manager verification")}</div>}
            {t.status === "completed" && <div className="mt-3 flex items-center gap-1.5 text-sm text-status-good"><ShieldCheck size={15} />{tI18n("Completed")}</div>}
            {t.status === "correction_required" && (
              <div className="mt-3 flex items-center gap-1.5 text-sm text-status-critical" data-testid={`task-correction-${t.instance_id}`}>
                <ShieldAlert size={15} />{tI18n("Correction required")}{t.verification?.reason ? `: ${t.verification.reason}` : ""}
              </div>
            )}
            {/* Actions */}
            {editable && (
              <div className="mt-3 flex flex-wrap gap-2">
                {canSubmit && <Button size="sm" onClick={() => submit(t)} disabled={busy === `submit:${t.instance_id}`} data-testid={`task-submit-${t.instance_id}`}><Send size={14} className="mr-1" />{tI18n("Submit")}</Button>}
                {!t.exception && <Button size="sm" variant="outline" onClick={() => { setExcFor(t.instance_id); setExcReason(""); }} data-testid={`task-report-exception-${t.instance_id}`}><AlertTriangle size={14} className="mr-1" />{tI18n("Report blocker")}</Button>}
                {t.exception && !t.linked_action_id && <Button size="sm" variant="outline" onClick={() => createAction(t)} data-testid={`task-create-action-${t.instance_id}`}><LinkIcon size={14} className="mr-1" />{tI18n("Create Action")}</Button>}
                {t.linked_action_id && <span className="flex items-center gap-1 text-xs text-status-info"><LinkIcon size={13} />{tI18n("Action linked")}</span>}
              </div>
            )}
            {excFor === t.instance_id && (
              <div className="mt-3 rounded-md border border-border p-3" data-testid={`task-exception-form-${t.instance_id}`}>
                <Textarea value={excReason} onChange={(e) => setExcReason(e.target.value)} placeholder={tI18n("Describe what's blocking this task…")} data-testid={`task-exception-reason-${t.instance_id}`} />
                <div className="mt-2 flex gap-2">
                  <Button size="sm" onClick={() => reportException(t)} data-testid={`task-exception-submit-${t.instance_id}`}>{tI18n("Report")}</Button>
                  <Button size="sm" variant="ghost" onClick={() => setExcFor(null)}>{tI18n("Cancel")}</Button>
                </div>
              </div>
            )}
          </Card>)
        );
      })}
    </div>)
  );
};

export default function MyWork() {
  return (
    <div>
      <PageHeader title="My Work" subtitle="Tasks and checklists assigned to your store" testId="mywork-header" />
      <TaskExecution />
    </div>
  );
}
